import type { Signal } from '@angular/core';
import { CalendarEventTypeKey } from 'src/lib/constants/calendar-event';
import type { ClassroomStudentModel } from 'src/lib/services/api/classroom-manager/models/classroom-student.model';
import {
	ClassroomActivityType,
	type ClassroomActivityModel,
	type ClassroomPeriodModel,
} from 'src/lib/services/api/classroom-manager/models/classroom.model';
import type { MeetupManagementListItemModel } from 'src/lib/services/api/organizations/meetups/meetup-management.model';
import type { StudentCurrentCourseModel } from 'src/lib/services/api/students/classes/current-courses/student-current-course.model';
import type {
	StudentCourseActivitiesModel,
	StudentCourseActivityFolderModel,
} from 'src/lib/services/api/students/classes/student-course.model';
import { parseZoned } from 'src/lib/utilities/temporal';
import { type WonderCharacterSubjectKey } from 'src/lib/utilities/wonder/wonder-character.utilities';
import 'temporal-polyfill/global';
import { firstBy } from 'thenby';
import { getWonderMeetupOccurrenceKey } from './wonder-activity-meetup.utilities';
import {
	getWonderActivitySubjectKey,
	WonderActivityScheduleCourseActivities,
	WonderActivityScheduleMonth,
	WonderActivityScheduleWeek,
	WonderCourseHubScheduleItem,
	WonderCourseLessonScheduleItem,
	WonderMeetupScheduleItem,
	WonderNoSchoolScheduleItem,
	WonderPodScheduleItem,
	WonderRecessScheduleItem,
	WonderSkillBuildersScheduleItem,
	WonderStudyScheduleItem,
	WonderUnstructuredScheduleItem,
	WonderWeeklyProgressDay,
	WonderWeeklyProgressItem,
	type WonderActivityScheduleItem,
} from './wonder-activity.model';

export interface WonderActivityAssignmentSlot {
	periodId: number | null;
	scheduleOrder: number;
	trackId: string;
}

export interface WonderActivityAssignmentDay {
	course: StudentCurrentCourseModel;
	date: Temporal.PlainDate;
	day: number;
	label: string;
	registrationId: number;
	slots: WonderActivityAssignmentSlot[];
	subjectKey: WonderCharacterSubjectKey;
}

interface WonderPacedAssignmentBundle {
	activities: StudentCourseActivityFolderModel[];
	anchor: StudentCourseActivityFolderModel;
}

export interface WonderPacedAssignmentDay extends WonderActivityAssignmentDay {
	activities: StudentCourseActivityFolderModel[];
	targetActivity: StudentCourseActivityFolderModel | null;
}

interface WonderCalendarEventSource {
	end_date?: unknown;
	start_date?: unknown;
	title?: unknown;
	type?: unknown;
}

const noCourseworkEventTitle = 'no coursework';
const weekdayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

export function selectPrimaryWonderActivityScheduleItem(
	candidates: readonly WonderActivityScheduleItem[],
): WonderActivityScheduleItem | undefined {
	return [...candidates].sort(
		firstBy<WonderActivityScheduleItem, number>((item) =>
			getWonderActivityPriority(item),
		)
			.thenBy(
				(item: WonderActivityScheduleItem) =>
					item.endDate.toInstant().epochMilliseconds,
			)
			.thenBy(
				(item: WonderActivityScheduleItem) =>
					item.startDate.toInstant().epochMilliseconds,
			)
			.thenBy((item: WonderActivityScheduleItem) => item.trackId),
	)[0];
}

export function buildActivityScheduleWeek(
	today: Temporal.ZonedDateTime,
	classroom: ClassroomStudentModel | null,
	currentCourses: StudentCurrentCourseModel[],
	courseActivities: WonderActivityScheduleCourseActivities[],
	meetups: MeetupManagementListItemModel[],
	currentTime: Signal<Temporal.ZonedDateTime>,
	pacedAssignmentDays: readonly WonderPacedAssignmentDay[] = buildWonderPacedAssignmentDays(
		classroom,
		courseActivities,
		getWonderActivityScheduleTimezone(today, classroom),
	),
): WonderActivityScheduleWeek {
	const scheduleWeek = buildEmptyWonderActivityScheduleWeek();
	const timezone = getWonderActivityScheduleTimezone(today, classroom);
	const weekStart = getWonderActivityScheduleWeekStart(today, classroom);
	const weekEnd = weekStart.add({ days: 7 });
	const consumedMeetupKeys = new Set<string>();
	const pacedAssignmentLookup = buildPacedAssignmentLookup(pacedAssignmentDays);
	const classroomItems = buildClassroomScheduleItems(
		weekStart,
		timezone,
		classroom,
		currentCourses,
		pacedAssignmentLookup,
		meetups,
		consumedMeetupKeys,
		currentTime,
	);
	const meetupItems = buildStandaloneMeetupScheduleItems(
		weekStart,
		weekEnd,
		timezone,
		meetups,
		consumedMeetupKeys,
		currentTime,
	);
	const visibleClassroomItems = classroomItems.filter(
		(classroomItem) =>
			(classroom == null ||
				!isSchoolClosedDate(
					classroom,
					classroomItem.startDate.toPlainDate(),
					timezone,
				)) &&
			!meetupItems.some((meetupItem) =>
				scheduleItemsOverlap(classroomItem, meetupItem),
			),
	);
	const activityScheduleItems = [...visibleClassroomItems, ...meetupItems];

	for (const activityScheduleItem of activityScheduleItems) {
		scheduleWeek[activityScheduleItem.startDate.dayOfWeek].push(
			activityScheduleItem,
		);
	}

	for (const activityScheduleDay of Object.values(scheduleWeek)) {
		activityScheduleDay.sort(
			firstBy<WonderActivityScheduleItem, number>(
				(item) => item.startDate.toInstant().epochMilliseconds,
			)
				.thenBy(
					(item: WonderActivityScheduleItem) =>
						item.endDate.toInstant().epochMilliseconds,
				)
				.thenBy((item: WonderActivityScheduleItem) => item.trackId),
		);
	}

	for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
		const dayStart = weekStart.add({ days: dayIndex });
		const activityScheduleDay = scheduleWeek[dayStart.dayOfWeek];
		const hasSkillBuilders = activityScheduleDay.some(
			(item) => item instanceof WonderSkillBuildersScheduleItem,
		);
		const schoolClosedEvent =
			classroom == null
				? null
				: findSchoolClosedEventForDate(
						classroom,
						dayStart.toPlainDate(),
						timezone,
					);

		if (schoolClosedEvent != null) {
			activityScheduleDay.push(
				new WonderNoSchoolScheduleItem(
					dayStart,
					[...activityScheduleDay],
					currentTime,
					getSchoolClosedEventTitle(schoolClosedEvent),
				),
			);
		} else if (!hasSkillBuilders && activityScheduleDay.length > 0) {
			activityScheduleDay.push(
				new WonderSkillBuildersScheduleItem(activityScheduleDay, currentTime),
			);
		}
	}

	return scheduleWeek;
}

export function buildWonderWeeklyProgressDays(
	pacedAssignmentDays: readonly WonderPacedAssignmentDay[],
	classroom: ClassroomStudentModel | null,
	weekStart: Temporal.ZonedDateTime,
): WonderWeeklyProgressDay[] {
	const timezone = getWonderActivityScheduleTimezone(weekStart, classroom);
	const days: WonderWeeklyProgressDay[] = weekdayLabels.map((label, index) => {
		const date = weekStart.add({ days: index });
		const plainDate = date.toPlainDate();

		return {
			date,
			index,
			isInSession: isDateInClassroomSession(classroom, plainDate, timezone),
			isSchoolClosed:
				classroom != null && isSchoolClosedDate(classroom, plainDate, timezone),
			items: [],
			label,
		};
	});
	const weekStartDate = weekStart.toPlainDate();
	const weekEndDate = weekStartDate.add({ days: weekdayLabels.length });

	for (const pacedAssignmentDay of pacedAssignmentDays) {
		if (
			Temporal.PlainDate.compare(pacedAssignmentDay.date, weekStartDate) < 0 ||
			Temporal.PlainDate.compare(pacedAssignmentDay.date, weekEndDate) >= 0
		) {
			continue;
		}

		const dayIndex = pacedAssignmentDay.date.dayOfWeek - 1;
		if (dayIndex < 0 || dayIndex >= days.length) {
			continue;
		}

		pacedAssignmentDay.activities.forEach((courseItem, index) => {
			const firstSlot = pacedAssignmentDay.slots[0];

			days[dayIndex].items.push(
				buildProgressItem(
					pacedAssignmentDay,
					courseItem,
					dayIndex,
					firstSlot.scheduleOrder * 1000 + index,
				),
			);
		});
	}

	return days.map((day) => ({
		...day,
		items: sortDayItems(day.items),
	}));
}

export function hasWonderWeeklyProgressSessionDay(
	classroom: ClassroomStudentModel | null,
	weekStart: Temporal.ZonedDateTime,
): boolean {
	const timezone = getWonderActivityScheduleTimezone(weekStart, classroom);

	return weekdayLabels.some((_label, index) =>
		isDateInClassroomSession(
			classroom,
			weekStart.add({ days: index }).toPlainDate(),
			timezone,
		),
	);
}

export function getWonderActivityDayInTimezone(
	dateTime: Temporal.ZonedDateTime,
	timezone: string,
): number {
	return dateTime.withTimeZone(timezone).dayOfWeek;
}

export function getWonderActivitySubjectIconPath(
	subjectKey: WonderCharacterSubjectKey,
): string {
	return `/wonder/courses/course_subject_${subjectKey}.png`;
}

export function getWonderActivityScheduleMonths(
	today: Temporal.ZonedDateTime,
	classroom: ClassroomStudentModel | null,
): WonderActivityScheduleMonth[] {
	const weekStart = getWonderActivityScheduleWeekStart(today, classroom);
	const months = new Map<string, WonderActivityScheduleMonth>();

	for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
		const date = weekStart.add({ days: dayIndex });
		months.set(`${date.year}:${date.month}`, {
			month: date.month,
			year: date.year,
		});
	}

	return [...months.values()];
}

export function getWonderActivityScheduleTimezone(
	today: Temporal.ZonedDateTime,
	classroom: ClassroomStudentModel | null,
): string {
	return classroom?.classroom?.timezone?.trim() || today.timeZoneId;
}

export function getWonderActivityScheduleWeekStart(
	today: Temporal.ZonedDateTime,
	classroom: ClassroomStudentModel | null,
): Temporal.ZonedDateTime {
	return getActivityScheduleWeekStart(
		today,
		getWonderActivityScheduleTimezone(today, classroom),
	);
}

export function isWonderActivityDateInClassroomSession(
	classroom: ClassroomStudentModel,
	dateTime: Temporal.ZonedDateTime,
): boolean {
	const timezone = getWonderActivityScheduleTimezone(dateTime, classroom);

	return isDateInClassroomSession(
		classroom,
		dateTime.withTimeZone(timezone).toPlainDate(),
		timezone,
	);
}

export function getWonderActivityWeekStart(
	dateTime: Temporal.ZonedDateTime,
): Temporal.ZonedDateTime {
	let weekStart = dateTime
		.with({
			hour: 1,
			microsecond: 0,
			millisecond: 0,
			minute: 0,
			nanosecond: 0,
			second: 0,
		})
		.subtract({ days: dateTime.dayOfWeek - 1 });

	if (Temporal.ZonedDateTime.compare(dateTime, weekStart) < 0) {
		weekStart = weekStart.subtract({ days: 7 });
	}

	return weekStart;
}

export function getNextWonderPacedActivityForDay(
	classroom: ClassroomStudentModel | null,
	courseActivities: WonderActivityScheduleCourseActivities[],
	timezone: string,
	registrationId: number,
	activityId: string,
	pacedAssignmentDays: readonly WonderPacedAssignmentDay[] = buildWonderPacedAssignmentDays(
		classroom,
		courseActivities,
		timezone,
	),
): StudentCourseActivityFolderModel | null {
	const pacedAssignmentDay = pacedAssignmentDays.find(
		(day) =>
			day.registrationId === registrationId &&
			day.activities.some((activity) => activity.id === activityId),
	);

	if (pacedAssignmentDay == null) {
		return null;
	}

	const currentActivityIndex = pacedAssignmentDay.activities.findIndex(
		(activity) => activity.id === activityId,
	);

	return (
		pacedAssignmentDay.activities
			.slice(currentActivityIndex + 1)
			.find(
				(activity) => !activity.locked && !isCourseItemComplete(activity),
			) ?? null
	);
}

function buildEmptyWonderActivityScheduleWeek(): WonderActivityScheduleWeek {
	return {
		1: [],
		2: [],
		3: [],
		4: [],
		5: [],
		6: [],
		7: [],
	};
}

function getActivityScheduleWeekStart(
	today: Temporal.ZonedDateTime,
	timezone: string,
): Temporal.ZonedDateTime {
	const zonedToday = today.withTimeZone(timezone);
	const monday = zonedToday
		.subtract({ days: zonedToday.dayOfWeek - 1 })
		.toPlainDate();

	return monday
		.toPlainDateTime({
			hour: 0,
			microsecond: 0,
			millisecond: 0,
			minute: 0,
			nanosecond: 0,
			second: 0,
		})
		.toZonedDateTime(timezone);
}

function buildClassroomScheduleItems(
	weekStart: Temporal.ZonedDateTime,
	timezone: string,
	classroom: ClassroomStudentModel | null,
	currentCourses: StudentCurrentCourseModel[],
	pacedAssignmentLookup: Map<string, WonderPacedAssignmentDay>,
	meetups: MeetupManagementListItemModel[],
	consumedMeetupKeys: Set<string>,
	currentTime: Signal<Temporal.ZonedDateTime>,
): WonderActivityScheduleItem[] {
	const periods = classroom?.classroom?.periods ?? [];
	const items: WonderActivityScheduleItem[] = [];

	for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
		const dayStart = weekStart.add({ days: dayIndex });
		const isInSession = isDateInClassroomSession(
			classroom,
			dayStart.toPlainDate(),
			timezone,
		);

		for (const period of periods) {
			const periodDates = buildPeriodDateTimes(period, dayStart, timezone);

			if (periodDates == null) {
				continue;
			}

			for (const activity of period.activities ?? []) {
				if (!isInSession || !activity.days?.includes(dayStart.dayOfWeek)) {
					continue;
				}

				const item = buildClassroomScheduleItem(
					activity,
					period,
					periodDates.startDate,
					periodDates.endDate,
					currentCourses,
					pacedAssignmentLookup,
					meetups,
					consumedMeetupKeys,
					timezone,
					currentTime,
				);

				if (item != null) {
					items.push(item);
				}
			}
		}
	}

	return items;
}

function buildClassroomScheduleItem(
	activity: ClassroomActivityModel,
	period: ClassroomPeriodModel,
	startDate: Temporal.ZonedDateTime,
	endDate: Temporal.ZonedDateTime,
	currentCourses: StudentCurrentCourseModel[],
	pacedAssignmentLookup: Map<string, WonderPacedAssignmentDay>,
	meetups: MeetupManagementListItemModel[],
	consumedMeetupKeys: Set<string>,
	timezone: string,
	currentTime: Signal<Temporal.ZonedDateTime>,
): WonderActivityScheduleItem | null {
	const id = buildClassroomActivityId(activity, period, startDate.dayOfWeek);

	if (activity.type === ClassroomActivityType.YourTurn) {
		const course = findCourseForActivity(activity, currentCourses);
		const trackId = `courseLesson:${id}`;
		const pacedAssignmentDay =
			course == null
				? null
				: (pacedAssignmentLookup.get(
						buildPacedAssignmentDayKey(
							course.registration_id,
							startDate.toPlainDate(),
						),
					) ?? null);

		if (
			course == null ||
			pacedAssignmentDay == null ||
			!pacedAssignmentDay.slots.some((slot) => slot.trackId === trackId)
		) {
			return null;
		}

		return new WonderCourseLessonScheduleItem(
			startDate,
			endDate,
			id,
			course,
			pacedAssignmentDay.targetActivity,
			pacedAssignmentDay.activities,
			activity,
			period,
			currentTime,
		);
	}

	if (activity.type === ClassroomActivityType.LearningHub) {
		const course = findCourseForActivity(activity, currentCourses);
		const meetup = findMeetupForClassroomActivity(
			activity,
			startDate,
			endDate,
			timezone,
			meetups,
			consumedMeetupKeys,
		);

		if (course == null || meetup == null) {
			return null;
		}

		consumeMeetup(consumedMeetupKeys, meetup);

		return new WonderCourseHubScheduleItem(
			startDate,
			endDate,
			id,
			course,
			meetup,
			activity,
			period,
			currentTime,
		);
	}

	if (activity.type === ClassroomActivityType.PodHub) {
		const meetup = findMeetupForClassroomActivity(
			activity,
			startDate,
			endDate,
			timezone,
			meetups,
			consumedMeetupKeys,
		);

		if (meetup == null) {
			return null;
		}

		consumeMeetup(consumedMeetupKeys, meetup);

		return new WonderPodScheduleItem(
			startDate,
			endDate,
			id,
			meetup,
			activity,
			period,
			currentTime,
		);
	}

	if (activity.type === ClassroomActivityType.Unstructured) {
		return new WonderUnstructuredScheduleItem(
			startDate,
			endDate,
			id,
			activity,
			period,
			activity.description?.trim() || 'Unstructured',
			currentTime,
		);
	}

	if (activity.type === ClassroomActivityType.Recess) {
		return new WonderRecessScheduleItem(
			startDate,
			endDate,
			id,
			activity,
			period,
			activity.description?.trim() || 'Recess',
			currentTime,
		);
	}

	if (activity.type === ClassroomActivityType.Study) {
		return new WonderStudyScheduleItem(
			startDate,
			endDate,
			id,
			activity,
			period,
			activity.description?.trim() || 'Study',
			currentTime,
		);
	}

	return null;
}

function buildPeriodDateTimes(
	period: ClassroomPeriodModel,
	dayStart: Temporal.ZonedDateTime,
	timezone: string,
): {
	endDate: Temporal.ZonedDateTime;
	startDate: Temporal.ZonedDateTime;
} | null {
	const startTime = parseScheduleDateTime(period.start_time, timezone);
	const endTime = parseScheduleDateTime(period.end_time, timezone);

	if (startTime == null || endTime == null) {
		return null;
	}

	const startDate = buildDateTimeForDay(dayStart, startTime, timezone);
	let endDate = buildDateTimeForDay(dayStart, endTime, timezone);

	if (Temporal.ZonedDateTime.compare(endDate, startDate) <= 0) {
		endDate = endDate.add({ days: 1 });
	}

	return {
		endDate,
		startDate,
	};
}

function parseScheduleDateTime(
	value: unknown,
	timezone: string,
): Temporal.ZonedDateTime | null {
	if (value instanceof Date) {
		try {
			return Temporal.Instant.fromEpochMilliseconds(
				value.getTime(),
			).toZonedDateTimeISO(timezone);
		} catch {
			return null;
		}
	}

	if (typeof value === 'string') {
		const match = value.match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/i);
		if (match) {
			let h = parseInt(match[1], 10);
			const m = parseInt(match[2], 10);
			const period = match[3]?.toLowerCase();
			if (period === 'pm' && h !== 12) h += 12;
			if (period === 'am' && h === 12) h = 0;
			try {
				return Temporal.Now.zonedDateTimeISO(timezone).withPlainTime({
					hour: h,
					minute: m,
				});
			} catch {
				return null;
			}
		}
	}

	return parseZoned(value, timezone);
}

function buildDateTimeForDay(
	dayStart: Temporal.ZonedDateTime,
	time: Temporal.ZonedDateTime,
	timezone: string,
): Temporal.ZonedDateTime {
	return dayStart
		.toPlainDate()
		.toPlainDateTime({
			hour: time.hour,
			microsecond: time.microsecond,
			millisecond: time.millisecond,
			minute: time.minute,
			nanosecond: time.nanosecond,
			second: time.second,
		})
		.toZonedDateTime(timezone);
}

function buildClassroomActivityId(
	activity: ClassroomActivityModel,
	period: ClassroomPeriodModel,
	dayOfWeek: number,
): string {
	return `${dayOfWeek}:${period.period_id}:${activity.activity_id ?? activity.type}`;
}

function findCourseForActivity(
	activity: ClassroomActivityModel,
	currentCourses: StudentCurrentCourseModel[],
): StudentCurrentCourseModel | null {
	const courseById =
		activity.course_id == null
			? null
			: currentCourses.find(
					(course) => course.course_id === activity.course_id,
				);

	if (courseById != null) {
		return courseById;
	}

	const activitySubjectKey = getWonderActivitySubjectKey(activity.course_name);

	if (activitySubjectKey == null) {
		return null;
	}

	return (
		currentCourses.find(
			(course) =>
				getWonderActivitySubjectKey(course.subject) === activitySubjectKey,
		) ?? null
	);
}

function findMeetupForClassroomActivity(
	activity: ClassroomActivityModel,
	startDate: Temporal.ZonedDateTime,
	endDate: Temporal.ZonedDateTime,
	timezone: string,
	meetups: MeetupManagementListItemModel[],
	consumedMeetupKeys: Set<string>,
): MeetupManagementListItemModel | null {
	const associatedMeetupId = activity.associated_meetup_id;

	if (associatedMeetupId == null) {
		return null;
	}

	// Classroom associations identify recurring series, not individual
	// occurrences. Links, hosts, and occurrence IDs are not substitutes.
	return (
		meetups.find(
			(meetup) =>
				isHubMeetup(meetup) &&
				meetup.gathering_type === activity.type &&
				isScheduledMeetup(meetup) &&
				!isMeetupConsumed(consumedMeetupKeys, meetup) &&
				meetup.scheduled_meetup_id === associatedMeetupId &&
				meetupMatchesClassroomActivityDateRange(
					meetup,
					startDate,
					endDate,
					timezone,
				),
		) ?? null
	);
}

function buildStandaloneMeetupScheduleItems(
	weekStart: Temporal.ZonedDateTime,
	weekEnd: Temporal.ZonedDateTime,
	timezone: string,
	meetups: MeetupManagementListItemModel[],
	consumedMeetupKeys: Set<string>,
	currentTime: Signal<Temporal.ZonedDateTime>,
): WonderMeetupScheduleItem[] {
	return meetups
		.filter((meetup) => isScheduledMeetup(meetup))
		.filter((meetup) => !isHubMeetup(meetup))
		.filter((meetup) => !isMeetupConsumed(consumedMeetupKeys, meetup))
		.map((meetup) =>
			buildStandaloneMeetupScheduleItem(meetup, timezone, currentTime),
		)
		.filter(
			(item): item is WonderMeetupScheduleItem =>
				item != null && scheduleItemStartsInRange(item, weekStart, weekEnd),
		);
}

function buildStandaloneMeetupScheduleItem(
	meetup: MeetupManagementListItemModel,
	timezone: string,
	currentTime: Signal<Temporal.ZonedDateTime>,
): WonderMeetupScheduleItem | null {
	const dates = buildMeetupDateTimes(meetup, timezone);

	if (dates == null) {
		return null;
	}

	return new WonderMeetupScheduleItem(
		dates.startDate,
		dates.endDate,
		`meetup:${meetup.id}:${dates.startDate.toInstant().epochMilliseconds}`,
		meetup,
		currentTime,
	);
}

function buildMeetupDateTimes(
	meetup: MeetupManagementListItemModel,
	timezone: string,
): {
	endDate: Temporal.ZonedDateTime;
	startDate: Temporal.ZonedDateTime;
} | null {
	const startDate = buildDateTimeFromDate(
		meetup.planned_arrival_date ?? meetup.start_date,
		timezone,
	);
	const endDate = buildDateTimeFromDate(
		meetup.planned_departure_date ?? meetup.end_date,
		timezone,
	);

	if (startDate == null || endDate == null) {
		return null;
	}

	if (Temporal.ZonedDateTime.compare(endDate, startDate) <= 0) {
		return null;
	}

	return {
		endDate,
		startDate,
	};
}

function buildDateTimeFromDate(
	date: Date | null | undefined,
	timezone: string,
): Temporal.ZonedDateTime | null {
	return date == null
		? null
		: Temporal.Instant.fromEpochMilliseconds(date.getTime()).toZonedDateTimeISO(
				timezone,
			);
}

function isScheduledMeetup(meetup: MeetupManagementListItemModel): boolean {
	return isHubMeetup(meetup)
		? meetup.active === true
		: meetup.rsvp === true && meetup.attendee_status >= 0;
}

function isHubMeetup(meetup: MeetupManagementListItemModel): boolean {
	return (
		meetup.gathering_type === 'learning_hub' ||
		meetup.gathering_type === 'pod_hub'
	);
}

function meetupMatchesClassroomActivityDateRange(
	meetup: MeetupManagementListItemModel,
	startDate: Temporal.ZonedDateTime,
	endDate: Temporal.ZonedDateTime,
	timezone: string,
): boolean {
	const meetupStartDate = buildDateTimeFromDate(meetup.start_date, timezone);
	const meetupEndDate = buildDateTimeFromDate(meetup.end_date, timezone);

	return (
		meetupStartDate != null &&
		meetupEndDate != null &&
		Temporal.PlainDate.compare(
			meetupStartDate.toPlainDate(),
			startDate.toPlainDate(),
		) === 0 &&
		dateRangesOverlap(meetupStartDate, meetupEndDate, startDate, endDate)
	);
}

function scheduleItemsOverlap(
	left: WonderActivityScheduleItem,
	right: WonderActivityScheduleItem,
): boolean {
	return dateRangesOverlap(
		left.startDate,
		left.endDate,
		right.startDate,
		right.endDate,
	);
}

function scheduleItemStartsInRange(
	item: WonderActivityScheduleItem,
	startDate: Temporal.ZonedDateTime,
	endDate: Temporal.ZonedDateTime,
): boolean {
	return (
		Temporal.ZonedDateTime.compare(item.startDate, startDate) >= 0 &&
		Temporal.ZonedDateTime.compare(item.startDate, endDate) < 0
	);
}

function dateRangesOverlap(
	leftStartDate: Temporal.ZonedDateTime,
	leftEndDate: Temporal.ZonedDateTime,
	rightStartDate: Temporal.ZonedDateTime,
	rightEndDate: Temporal.ZonedDateTime,
): boolean {
	return (
		Temporal.ZonedDateTime.compare(leftStartDate, rightEndDate) < 0 &&
		Temporal.ZonedDateTime.compare(leftEndDate, rightStartDate) > 0
	);
}

function consumeMeetup(
	consumedMeetupKeys: Set<string>,
	meetup: MeetupManagementListItemModel,
): void {
	consumedMeetupKeys.add(getWonderMeetupOccurrenceKey(meetup));
}

function isMeetupConsumed(
	consumedMeetupKeys: Set<string>,
	meetup: MeetupManagementListItemModel,
): boolean {
	return consumedMeetupKeys.has(getWonderMeetupOccurrenceKey(meetup));
}

function getWonderActivityPriority(item: WonderActivityScheduleItem): number {
	if (item.source === 'meetup') {
		return 0;
	}

	switch (item.type) {
		case 'courseHub':
			return 1;
		case 'pod':
			return 2;
		case 'courseLesson':
			return 3;
		case 'recess':
		case 'study':
		case 'unstructured':
			return 4;
		case 'noSchool':
		case 'skillBuilders':
			return 5;
		default:
			return 6;
	}
}

function buildPacedAssignmentLookup(
	pacedAssignmentDays: readonly WonderPacedAssignmentDay[],
): Map<string, WonderPacedAssignmentDay> {
	return new Map(
		pacedAssignmentDays.map((pacedAssignmentDay) => [
			buildPacedAssignmentDayKey(
				pacedAssignmentDay.registrationId,
				pacedAssignmentDay.date,
			),
			pacedAssignmentDay,
		]),
	);
}

export function buildWonderPacedAssignmentDays(
	classroom: ClassroomStudentModel | null,
	courseActivities: WonderActivityScheduleCourseActivities[],
	timezone: string,
): WonderPacedAssignmentDay[] {
	if (classroom == null) {
		return [];
	}

	const currentCourses = courseActivities.map(
		(courseData) => courseData.course,
	);
	const pacedAssignmentDays: WonderPacedAssignmentDay[] = [];

	for (const courseData of courseActivities) {
		const course = courseData.course;
		const subjectKey = getWonderActivitySubjectKey(course.subject);

		const candidateDays = buildAssignmentCandidateDays(
			classroom,
			currentCourses,
			course,
			subjectKey,
			timezone,
		);
		const assignmentBundles = buildPacedAssignmentBundles(
			courseData.activities,
		);
		const distribution = distributeAssignmentCounts(
			assignmentBundles.length,
			candidateDays.length,
		);
		let bundleIndex = 0;

		candidateDays.forEach((candidateDay, candidateIndex) => {
			const bundleCount = distribution[candidateIndex] ?? 0;
			const bundles = assignmentBundles.slice(
				bundleIndex,
				bundleIndex + bundleCount,
			);
			bundleIndex += bundleCount;

			if (!bundles.length) {
				return;
			}

			const activities = bundles.flatMap((bundle) => bundle.activities);

			pacedAssignmentDays.push({
				...candidateDay,
				activities,
				targetActivity: getPacedTargetActivity(activities),
			});
		});
	}

	return pacedAssignmentDays;
}

function buildAssignmentCandidateDays(
	classroom: ClassroomStudentModel,
	currentCourses: StudentCurrentCourseModel[],
	course: StudentCurrentCourseModel,
	subjectKey: WonderCharacterSubjectKey,
	timezone: string,
): WonderActivityAssignmentDay[] {
	const startDate = classroom.session.registration_start
		.withTimeZone(timezone)
		.toPlainDate();
	const endDate = classroom.session.registration_end
		.withTimeZone(timezone)
		.toPlainDate();
	const candidateDays: WonderActivityAssignmentDay[] = [];

	for (
		let date = startDate;
		Temporal.PlainDate.compare(date, endDate) <= 0;
		date = date.add({ days: 1 })
	) {
		if (
			!isWeeklyProgressWeekday(date) ||
			isAssignmentPacingBlockedDate(classroom, date, timezone)
		) {
			continue;
		}

		const candidateDay = findYourTurnSlotsForCourseDate(
			classroom,
			currentCourses,
			course,
			subjectKey,
			date,
			timezone,
		);

		if (candidateDay != null) {
			candidateDays.push(candidateDay);
		}
	}

	return candidateDays;
}

function findYourTurnSlotsForCourseDate(
	classroom: ClassroomStudentModel,
	currentCourses: StudentCurrentCourseModel[],
	course: StudentCurrentCourseModel,
	subjectKey: WonderCharacterSubjectKey,
	date: Temporal.PlainDate,
	timezone: string,
): WonderActivityAssignmentDay | null {
	const dayStart = plainDateToZonedDateTime(date, timezone);
	const matchedSlots: WonderActivityAssignmentSlot[] = [];

	for (const period of classroom.classroom?.periods ?? []) {
		const periodDates = buildPeriodDateTimes(period, dayStart, timezone);

		if (periodDates == null) {
			continue;
		}

		for (const activity of period.activities ?? []) {
			if (
				activity.type !== ClassroomActivityType.YourTurn ||
				!activity.days?.includes(date.dayOfWeek)
			) {
				continue;
			}

			const matchedCourse = findCourseForActivity(activity, currentCourses);
			if (matchedCourse?.registration_id !== course.registration_id) {
				continue;
			}

			const id = buildClassroomActivityId(activity, period, date.dayOfWeek);

			matchedSlots.push({
				periodId: period.period_id ?? null,
				scheduleOrder: periodDates.startDate.toInstant().epochMilliseconds,
				trackId: `courseLesson:${id}`,
			});
		}
	}

	matchedSlots.sort((left, right) => left.scheduleOrder - right.scheduleOrder);

	return matchedSlots.length === 0
		? null
		: {
				course,
				date,
				day: date.dayOfWeek,
				label: course.subject?.trim() || course.title?.trim() || 'Course',
				registrationId: course.registration_id,
				slots: matchedSlots,
				subjectKey,
			};
}

function buildPacedAssignmentBundles(
	activitiesModel: StudentCourseActivitiesModel | null,
): WonderPacedAssignmentBundle[] {
	const activities = extractCourseItems(activitiesModel);
	const bundles: WonderPacedAssignmentBundle[] = [];
	let currentBundle: WonderPacedAssignmentBundle | null = null;
	let leadingActivities: StudentCourseActivityFolderModel[] = [];

	for (const activity of activities) {
		if (isDistributedAssignmentActivity(activity)) {
			currentBundle = {
				activities: [...leadingActivities, activity],
				anchor: activity,
			};
			bundles.push(currentBundle);
			leadingActivities = [];
			continue;
		}

		if (currentBundle != null) {
			currentBundle.activities.push(activity);
		} else {
			leadingActivities.push(activity);
		}
	}

	return bundles;
}

function extractCourseItems(
	activitiesModel: StudentCourseActivitiesModel | null,
): StudentCourseActivityFolderModel[] {
	return (activitiesModel?.folders ?? [])
		.flatMap((folder) => folder.activities ?? [])
		.filter((activity) => !activity.hidden);
}

function isDistributedAssignmentActivity(
	activity: StudentCourseActivityFolderModel,
): boolean {
	const type = activity.actual_type?.trim().toLowerCase();
	return type === 'scorm' || type === 'lti';
}

function getPacedTargetActivity(
	activities: StudentCourseActivityFolderModel[],
): StudentCourseActivityFolderModel | null {
	return (
		activities.find(
			(activity) => !activity.locked && !isCourseItemComplete(activity),
		) ?? null
	);
}

function distributeAssignmentCounts(
	activityCount: number,
	dayCount: number,
): number[] {
	if (dayCount <= 0) {
		return [];
	}

	const distribution = Array.from({ length: dayCount }, () => 0);

	if (activityCount <= 0) {
		return distribution;
	}

	if (activityCount >= dayCount) {
		return distribution.map(
			(_count, dayIndex) =>
				Math.round(((dayIndex + 1) * activityCount) / dayCount) -
				Math.round((dayIndex * activityCount) / dayCount),
		);
	}

	for (let index = 0; index < activityCount; index++) {
		const dayIndex = Math.min(
			dayCount - 1,
			Math.round((index * dayCount) / activityCount),
		);

		distribution[dayIndex] += 1;
	}

	return distribution;
}

function buildPacedAssignmentDayKey(
	registrationId: number | null | undefined,
	date: Temporal.PlainDate,
): string {
	return `${registrationId ?? 'none'}:${date.toString()}`;
}

function isWeeklyProgressWeekday(date: Temporal.PlainDate): boolean {
	return date.dayOfWeek >= 1 && date.dayOfWeek <= weekdayLabels.length;
}

function isDateInClassroomSession(
	classroom: ClassroomStudentModel | null,
	date: Temporal.PlainDate,
	timezone: string,
): boolean {
	if (classroom == null) {
		return false;
	}

	const sessionStartDate = classroom.session.registration_start
		.withTimeZone(timezone)
		.toPlainDate();
	const sessionEndDate = classroom.session.registration_end
		.withTimeZone(timezone)
		.toPlainDate();

	return (
		Temporal.PlainDate.compare(date, sessionStartDate) >= 0 &&
		Temporal.PlainDate.compare(date, sessionEndDate) <= 0
	);
}

function isAssignmentPacingBlockedDate(
	classroom: ClassroomStudentModel,
	date: Temporal.PlainDate,
	timezone: string,
): boolean {
	return (
		isSchoolClosedDate(classroom, date, timezone) ||
		isNoCourseworkDate(classroom, date, timezone)
	);
}

function isNoCourseworkDate(
	classroom: ClassroomStudentModel,
	date: Temporal.PlainDate,
	timezone: string,
): boolean {
	return (classroom.program?.calendar ?? []).some((event) =>
		calendarEventBlocksCourseworkDate(event, date, timezone),
	);
}

function isSchoolClosedDate(
	classroom: ClassroomStudentModel,
	date: Temporal.PlainDate,
	timezone: string,
): boolean {
	return findSchoolClosedEventForDate(classroom, date, timezone) != null;
}

function findSchoolClosedEventForDate(
	classroom: ClassroomStudentModel,
	date: Temporal.PlainDate,
	timezone: string,
): WonderCalendarEventSource | null {
	return (
		(classroom.program?.calendar ?? []).find(
			(event): event is WonderCalendarEventSource =>
				calendarEventClosesDate(event, date, timezone),
		) ?? null
	);
}

function getSchoolClosedEventTitle(event: WonderCalendarEventSource): string {
	return typeof event.title === 'string' && event.title.trim() !== ''
		? event.title.trim()
		: 'School Closed';
}

function calendarEventClosesDate(
	value: unknown,
	date: Temporal.PlainDate,
	timezone: string,
): value is WonderCalendarEventSource {
	if (!isCalendarEventSource(value)) {
		return false;
	}

	if (value.type !== CalendarEventTypeKey.SchoolClosed) {
		return false;
	}

	return calendarEventCoversDate(value, date, timezone);
}

function calendarEventBlocksCourseworkDate(
	value: unknown,
	date: Temporal.PlainDate,
	timezone: string,
): value is WonderCalendarEventSource {
	if (!isCalendarEventSource(value)) {
		return false;
	}

	if (
		value.type !== CalendarEventTypeKey.Misc ||
		typeof value.title !== 'string' ||
		value.title.trim().toLowerCase() !== noCourseworkEventTitle
	) {
		return false;
	}

	return calendarEventCoversDate(value, date, timezone);
}

function calendarEventCoversDate(
	value: WonderCalendarEventSource,
	date: Temporal.PlainDate,
	timezone: string,
): boolean {
	const startDate = parseCalendarEventDateTime(
		value.start_date,
		timezone,
		'start',
	);
	const rawEndDate = parseCalendarEventDateTime(
		value.end_date,
		timezone,
		'end',
	);

	if (startDate == null) {
		return false;
	}

	const startPlainDate = startDate.toPlainDate();
	const endPlainDate =
		rawEndDate == null ||
		Temporal.ZonedDateTime.compare(rawEndDate, startDate) <= 0
			? startPlainDate
			: getCalendarEventEndDate(rawEndDate, startPlainDate);

	return (
		Temporal.PlainDate.compare(startPlainDate, date) <= 0 &&
		Temporal.PlainDate.compare(endPlainDate, date) >= 0
	);
}

function getCalendarEventEndDate(
	endDate: Temporal.ZonedDateTime,
	startPlainDate: Temporal.PlainDate,
): Temporal.PlainDate {
	const endPlainDate = endDate.toPlainDate();
	const endTime = endDate.toPlainTime();
	const isMidnight =
		endTime.hour === 0 &&
		endTime.microsecond === 0 &&
		endTime.millisecond === 0 &&
		endTime.minute === 0 &&
		endTime.nanosecond === 0 &&
		endTime.second === 0;

	if (
		isMidnight &&
		Temporal.PlainDate.compare(endPlainDate, startPlainDate) > 0
	) {
		return endPlainDate.subtract({ days: 1 });
	}

	return endPlainDate;
}

function isCalendarEventSource(
	value: unknown,
): value is WonderCalendarEventSource {
	return typeof value === 'object' && value != null;
}

function parseCalendarEventDateTime(
	value: unknown,
	timezone: string,
	boundary: 'end' | 'start',
): Temporal.ZonedDateTime | null {
	if (value instanceof Temporal.ZonedDateTime) {
		return value.withTimeZone(timezone);
	}

	if (value instanceof Date) {
		return buildDateTimeFromDate(value, timezone);
	}

	if (typeof value === 'string') {
		const dateOnlyMatch = value.match(/^(\d{4}-\d{2}-\d{2})$/);
		if (dateOnlyMatch) {
			try {
				const date = Temporal.PlainDate.from(dateOnlyMatch[1]);

				return date
					.toPlainDateTime(
						boundary === 'start'
							? {
									hour: 0,
									microsecond: 0,
									millisecond: 0,
									minute: 0,
									nanosecond: 0,
									second: 0,
								}
							: {
									hour: 23,
									microsecond: 999,
									millisecond: 999,
									minute: 59,
									nanosecond: 999,
									second: 59,
								},
					)
					.toZonedDateTime(timezone);
			} catch {
				return null;
			}
		}

		const zonedDateTime = parseZoned(value, timezone);
		if (zonedDateTime != null) {
			return zonedDateTime;
		}

		const date = new Date(value);
		if (Number.isFinite(date.getTime())) {
			return buildDateTimeFromDate(date, timezone);
		}
	}

	return parseZoned(value, timezone);
}

function plainDateToZonedDateTime(
	date: Temporal.PlainDate,
	timezone: string,
): Temporal.ZonedDateTime {
	return date
		.toPlainDateTime({
			hour: 0,
			microsecond: 0,
			millisecond: 0,
			minute: 0,
			nanosecond: 0,
			second: 0,
		})
		.toZonedDateTime(timezone);
}

function buildProgressItem(
	scheduledActivity: WonderActivityAssignmentDay,
	realActivity: StudentCourseActivityFolderModel,
	dayIndex: number,
	scheduleOrder: number,
): WonderWeeklyProgressItem {
	const completed = isCourseItemComplete(realActivity);
	const registrationId = scheduledActivity.registrationId;
	const route = [
		'/courses',
		registrationId,
		'activity-player',
		realActivity.id,
	];

	let gradeLabel =
		realActivity.graded_percent != null
			? `${Math.round(realActivity.graded_percent * 100)}%`
			: 'No grade yet';
	if (realActivity.is_excused) {
		gradeLabel = 'Excused';
	} else if (realActivity.pending_grade) {
		gradeLabel = 'Pending grade';
	}

	return {
		activity: realActivity,
		completed: completed,
		course: scheduledActivity.course,
		dayIndex,
		gradeLabel: gradeLabel,
		latestFeedback: realActivity.teacher_feedback
			? { message: realActivity.teacher_feedback }
			: null,
		locked: realActivity.locked,
		registrationId,
		route,
		scheduleOrder,
		scheduleTrackId: scheduledActivity.slots[0].trackId,
		statusClass: completed
			? 'completed'
			: realActivity.locked
				? 'locked'
				: 'open',
		statusIcon: completed
			? 'fas fa-check'
			: realActivity.locked
				? 'fas fa-lock'
				: 'fas fa-chevron-right',
		statusLabel: completed
			? 'Complete'
			: realActivity.locked
				? 'Locked'
				: 'Not complete',
		subjectKey: scheduledActivity.subjectKey,
		subjectLabel:
			scheduledActivity.course.subject?.trim() || scheduledActivity.label,
		trackId: `${dayIndex}:${scheduledActivity.subjectKey}:${realActivity.id}`,
	};
}

function sortDayItems(
	items: WonderWeeklyProgressItem[],
): WonderWeeklyProgressItem[] {
	return items
		.map((item, index) => ({ index, item }))
		.sort(
			(left, right) =>
				left.item.scheduleOrder - right.item.scheduleOrder ||
				left.index - right.index,
		)
		.map(({ item }) => item);
}

function isCourseItemComplete(
	activity: StudentCourseActivityFolderModel,
): boolean {
	if (activity.type === 'assignment') {
		return activity.uncompleted_assignment === false;
	}

	return (activity.time_spent ?? 0) > 0;
}
